import { useEffect, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { formatPocet } from "@/lib/format";
import type { CennikFacety } from "@/api/schemas";
import type {
  CennikSidebarFiltre,
  MaterialovyTab,
} from "@/components/cennik/cennikZalozky";

/**
 * Pohľady „Treba doriešiť" a zriedkavé filtre Cenníka.
 *
 * KOLO 197 (možnosť 1 zo stránky „Vzhľad cenníka"): fajky „Len …" z bočného
 * panela sa stali kapsulami nad tabuľkou. KOLO 199 (okno B): pohľady
 * Nedokončené / Nezaradené / Skontroluj cenu sú v ľavom menu (`CennikMenu`),
 * zapnuté filtre ako kapsule s ✕ v `CennikFiltre`. Tu ostali spoločné
 * definície, nech majú menu, mobil aj kapsule rovnaké texty a počty.
 */

/** Pohľady, z ktorých platí najviac jeden — počty ich rátajú bez seba. */
export type PohladUpozornenia = "nedokoncene" | "nezaradene" | "skontrolujCenu";

export const POHLADY_UPOZORNENI: {
  kluc: PohladUpozornenia;
  label: string;
  title: string;
  pocet: (f: CennikFacety) => number;
}[] = [
  {
    kluc: "nedokoncene",
    label: "Nedokončené",
    title: "Bez kódu alebo bez kategórie — nedá sa objednať alebo nemá odkiaľ vziať prácu a réžiu",
    pocet: (f) => f.nedokoncene ?? 0,
  },
  {
    kluc: "nezaradene",
    label: "Nezaradené",
    title: "Bez kategórie alebo s predvolenou kategóriou importu — dajú sa hromadne zaradiť",
    pocet: (f) => f.nezaradene ?? 0,
  },
  {
    kluc: "skontrolujCenu",
    label: "Skontroluj cenu",
    title: "Cenník dodávateľa cenu nemal, appka dala odhad z podobných položiek",
    pocet: (f) => f.skontroluj_cenu ?? 0,
  },
];

/** Zmena filtrov pri kliknutí na pohľad — zapnutý je najviac jeden. */
export function prepniPohlad(
  filtre: CennikSidebarFiltre,
  kluc: PohladUpozornenia,
): Partial<CennikSidebarFiltre> {
  return {
    nedokoncene: false,
    nezaradene: false,
    skontrolujCenu: false,
    [kluc]: !filtre[kluc],
  };
}

/** Zriedkavé filtre z „Ďalšie filtre ▾" — ktoré platia pre záložku. */
export type DalsiFilter = "lenVyradene" | "bezStruktury" | "lenDuplicity";

export function dalsieFiltreZalozky(
  tab: MaterialovyTab,
): { kluc: DalsiFilter; kratko: string }[] {
  return [
    { kluc: "lenVyradene", kratko: "len vyradené" },
    ...(tab !== "kovanie" ? [{ kluc: "bezStruktury" as const, kratko: "bez štruktúry" }] : []),
    ...(tab === "kovanie" ? [{ kluc: "lenDuplicity" as const, kratko: "len duplicity" }] : []),
  ];
}

type Props = {
  tab: MaterialovyTab;
  /** Počty z `GET /cennik/facety`; počas prvého načítania `undefined`. */
  facety: CennikFacety | undefined;
  filtre: CennikSidebarFiltre;
  onZmena: (zmena: Partial<CennikSidebarFiltre>) => void;
};

/** „Ďalšie filtre ▾" — zriedkavé filtre v rozbaľovacom paneli s fajkami. */
export function DalsieFiltre({ tab, facety, filtre, onZmena }: Props): JSX.Element {
  const pocetZapnutych = dalsieFiltreZalozky(tab).filter((d) => filtre[d.kluc]).length;
  const [otvorene, setOtvorene] = useState(false);
  const obal = useRef<HTMLDivElement>(null);

  // Klik mimo alebo Esc panel zavrie
  useEffect(() => {
    if (!otvorene) return;
    function mimo(e: MouseEvent): void {
      if (obal.current && !obal.current.contains(e.target as Node)) setOtvorene(false);
    }
    function esc(e: KeyboardEvent): void {
      if (e.key === "Escape") setOtvorene(false);
    }
    document.addEventListener("mousedown", mimo);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("mousedown", mimo);
      document.removeEventListener("keydown", esc);
    };
  }, [otvorene]);

  const polozky: {
    kluc: "lenVyradene" | "bezStruktury" | "lenDuplicity";
    label: string;
    pocet: number | undefined;
    title: string;
  }[] = [
    {
      kluc: "lenVyradene",
      label: "Len vyradené",
      pocet: facety?.vyradene,
      title: "Vyradené položky — dajú sa obnoviť",
    },
    ...(tab !== "kovanie"
      ? [
          {
            kluc: "bezStruktury" as const,
            label: "Bez štruktúry",
            pocet: facety?.bez_struktury,
            title: "Dekory a hrany bez štruktúry povrchu",
          },
        ]
      : []),
    // Ten istý výrobok u viacerých dodávateľov — kódy výrobcu majú len
    // Blum a Häfele, teda len kovanie (KOLO 166)
    ...(tab === "kovanie"
      ? [
          {
            kluc: "lenDuplicity" as const,
            label: "Len duplicity",
            pocet: facety?.duplicity,
            title: "Ten istý výrobok (kód výrobcu) aj u iného dodávateľa — štítok „aj u: …“",
          },
        ]
      : []),
  ];

  return (
    <div ref={obal} className="relative">
      <Button
        type="button"
        variant="outline"
        size="sm"
        aria-expanded={otvorene}
        onClick={() => setOtvorene((o) => !o)}
        // Ako ostatné tlačidlá v riadku filtrov (KOLO 199)
        className={
          "h-9 gap-1.5 px-3 text-sm font-medium" +
          (pocetZapnutych > 0 ? " border-primary bg-tandem-red-light text-tandem-red-dark" : "")
        }
      >
        Ďalšie filtre
        {pocetZapnutych > 0 ? (
          <span className="rounded-full bg-primary px-1.5 text-[11px] text-primary-foreground">
            {pocetZapnutych}
          </span>
        ) : null}
        <ChevronDown className="h-4 w-4" aria-hidden />
      </Button>
      {otvorene ? (
        <div
          className="absolute right-0 z-20 mt-1 grid w-64 gap-1 rounded-md border bg-popover p-2 text-popover-foreground shadow-md"
          aria-label="Ďalšie filtre"
        >
          {polozky.map((p) => (
            <label
              key={p.kluc}
              className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-accent"
              title={p.title}
            >
              <input
                type="checkbox"
                // Názov bez počtu — počet vedľa je len informácia
                aria-label={p.label}
                checked={filtre[p.kluc]}
                onChange={(e) => onZmena({ [p.kluc]: e.target.checked })}
                className="h-4 w-4 accent-primary"
              />
              {p.label}
              {p.pocet !== undefined ? (
                <span className="ml-auto font-mono text-xs text-muted-foreground">
                  {formatPocet(p.pocet)}
                </span>
              ) : null}
            </label>
          ))}
        </div>
      ) : null}
    </div>
  );
}
